import {
  TCancelOrderRequestBody,
  TExportOrdersParams,
  TFa,
  TOrder,
  TOrderFormatted,
  TOrderListAPIParams,
  TOrderSAPResponse,
  TPaymentMethod,
  TRetailerSalesOrderAddress,
  TReturnCompleteOrderRequestBody,
  TUpdateOrderBody,
} from '../utils/types/orders';
import { createTokenHeader } from '../utils/functions/headers';
import {
  TDeliverySchedulesImportResponse,
  TOrderStatusCount,
} from '../utils/types';
import { emptySplitApi } from './emptySplitApi';
import { toTitleCase } from '../utils/functions/string';
import { getFormattedDate } from '../utils/functions/common';

const getFormattedAddress = (
  address: TRetailerSalesOrderAddress[]
) => {
  let formattedAddress = '';
  if (address) {
    const shippingAddress = address.find(
      (data) => data.addressType === 'SHIPPING'
    );
    if (shippingAddress) {
      let addressFields = [
        shippingAddress.street,
        shippingAddress.stateName,
        shippingAddress.districtName,
        shippingAddress.tehshilName,
        shippingAddress.villageName,
        shippingAddress.pinCode,
      ];

      addressFields = addressFields.filter((data) => data);
      formattedAddress = addressFields.join(', ');
    }
  }
  return formattedAddress;
};

const getFormattedOrders: (
  list: TOrder[]
) => TOrderFormatted[] = (ordersList) => {
  return ordersList.map((order) => ({
    ...order,
    isSAPOrderFailed:
      !!order.retryCount &&
      !order.sapOrderId &&
      ![
        'Failed',
        'Cancelled',
        'Expired',
        'Delivered',
      ].includes(order.orderStatusString),
    netPrice: Number(order.netPrice?.toFixed(2)),
    customerName: toTitleCase(order.customerName),
    firmName: toTitleCase(order.firmName),
    faName: toTitleCase(order.faName),
    orderAddressFormatted: getFormattedAddress(
      order.retailerSalesOrderAddresses
    ),
  }));
};

const getSelectedPlantsUnion = (
  plantList: string[] | null | undefined,
  districtDepoMappingList: string[] | null | undefined
) => {
  const finalArr: string[] = [];
  districtDepoMappingList?.forEach((listString) => {
    listString?.split(',')?.map((plant) => {
      finalArr.push(plant);
    });
  });
  plantList?.forEach((plant) => {
    finalArr.push(plant);
  });

  return Array.from(new Set(finalArr)).join(',');
};

export const ordersService = emptySplitApi
  .enhanceEndpoints({
    addTagTypes: ['LIST', 'COUNT', 'DETAILS'],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getOrderHistoryList: builder.query<
        { data: TOrderFormatted[]; totalCount: number },
        TOrderListAPIParams
      >({
        query: ({
          pageNo,
          pageSize,
          searchString,
          plantList,
          statusList,
          orderType,
          startDate,
          endDate,
          plantRegionList,
          plantModelList,
          retailerId,
          pendingAllowed,
          districtDepoMappingList,
        }) => ({
          method: 'GET',
          url: 'retailers/order/list',
          params: {
            page: pageNo,
            size: pageSize,
            searchKey: searchString ? searchString : null,
            plantList: getSelectedPlantsUnion(
              plantList,
              districtDepoMappingList
            ),
            statusList: statusList?.toString(),
            orderType: orderType,
            startDate: getFormattedDate(startDate),
            endDate: getFormattedDate(endDate),
            plantRegionList:
              plantRegionList?.join(',') || null,
            plantModelList:
              plantModelList?.join(',') || null,
            retailerId: retailerId,
            sort: 'createdDate,desc',
            pendingAllowed: pendingAllowed,
          },
        }),
        transformResponse: (
          data: TOrder[],
          meta: Response
        ) => {
          const totalCount =
            meta.headers.get('X-total-count');
          return {
            data: getFormattedOrders(data),
            totalCount: Number(totalCount ? totalCount : 0),
          };
        },
        providesTags: ['LIST'],
      }),
      getOrdersStatusCount: builder.query<
        TOrderStatusCount[],
        boolean
      >({
        query: (pendingAllowed) => ({
          method: 'GET',
          url: 'retailers/orders/all-status/counts',
          params: {
            pendingAllowed: pendingAllowed,
          },
        }),
        providesTags: ['COUNT'],
      }),
      eddDelayImport: builder.mutation<
        TDeliverySchedulesImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'edd-delay/import',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['LIST', 'COUNT'],
      }),
      markOrdersForDispatch: builder.mutation<
        TDeliverySchedulesImportResponse,
        number[]
      >({
        query: (data) => {
          return {
            url: 'retailer/sales/orders/mark/rtd',
            method: 'POST',
            data,
          };
        },
        invalidatesTags: ['LIST', 'COUNT'],
      }),
      exportOrders: builder.query<
        { url?: string },
        TExportOrdersParams
      >({
        query: ({ pendingAllowed, ...restArgs }) => {
          return {
            url: 'retailers/orders/export',
            method: 'POST',
            data: {
              ...restArgs,
            },
            params: {
              pendingAllowed,
            },
          };
        },
      }),
      uploadInvoice: builder.mutation<
        TDeliverySchedulesImportResponse,
        { data: FormData; orderId: number }
      >({
        query: ({ data, orderId }) => {
          return {
            url: `retailer/order/${orderId}/invoice/upload`,
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['LIST', 'COUNT'],
      }),
      getFAList: builder.query<TFa[], number>({
        query: (enterpriseId) => ({
          method: 'GET',
          url: `fa/list/enterprise/${enterpriseId}`,
        }),
      }),
      updateOrder: builder.mutation<void, TUpdateOrderBody>(
        {
          query: (data) => ({
            method: 'PUT',
            url: 'retailer/sales/orders',
            data,
          }),
          invalidatesTags: ['LIST', 'COUNT', 'DETAILS'],
        }
      ),
      getOrderDetails: builder.query<TOrder, string>({
        query: (id) => ({
          method: 'GET',
          url: `retailer/order/${id}`,
        }),
        providesTags: ['DETAILS'],
      }),
      cancelOrder: builder.mutation<
        { message: string },
        TCancelOrderRequestBody
      >({
        query: (data) => ({
          method: 'POST',
          url: 'retailers/orders/cancel',
          data,
        }),
        invalidatesTags: ['DETAILS'],
      }),
      cancelOrderV2: builder.mutation<
        { message: string },
        TCancelOrderRequestBody
      >({
        query: (data) => ({
          method: 'POST',
          url: 'retailers/orders/cancel/v2',
          data,
        }),
        invalidatesTags: ['DETAILS'],
      }),
      orderActionDocumentUpload: builder.mutation<
        TDeliverySchedulesImportResponse,
        FormData
      >({
        query: (data) => {
          return {
            url: 'retailer/order/cancel/doc/upload',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['DETAILS'],
      }),
      returnCompleteOrder: builder.mutation<
        { message: string },
        TReturnCompleteOrderRequestBody
      >({
        query: (data) => ({
          method: 'POST',
          url: 'retailers/orders/return',
          data,
        }),
        invalidatesTags: ['DETAILS'],
      }),
      markPreInvoiced: builder.mutation<
        { message: string },
        { retailerSalesOrderId: number }
      >({
        query: (data) => ({
          method: 'POST',
          url: 'retailers/orders/pre-invoice',
          data,
        }),
        invalidatesTags: ['DETAILS'],
      }),
      getOrderFailureResponse: builder.query<void, string>({
        query: (orderId) => ({
          method: 'GET',
          url: `payment/failure/reason/${orderId}`,
        }),
      }),
      getAllPaymentMethods: builder.query<
        TPaymentMethod[],
        string
      >({
        query: (appOrderId) => ({
          method: 'GET',
          url: 'payment/v2/transactions',
          params: {
            appOrderId,
          },
        }),
      }),
      isOrderExpiryExtended: builder.query<boolean, number>(
        {
          query: (orderId) => ({
            method: 'GET',
            url: `retailer/order/${orderId}/isExpiryExtended`,
          }),
        }
      ),
      getAllOrdersForMasterId: builder.query<
        TOrder[],
        string
      >({
        query: (masterId) => ({
          method: 'GET',
          url: 'v4/retailer/orders',
          params: {
            masterAppOrderId: masterId,
            sort: 'createdDate,asc',
            page: 0,
            size: 20,
          },
        }),
      }),
      extendExpiry: builder.mutation<void, FormData>({
        query: (data) => {
          return {
            url: 'retailer/order/extend/expiry',
            method: 'POST',
            data,
            headers: createTokenHeader(),
          };
        },
        invalidatesTags: ['DETAILS'],
      }),
      getInvoice: builder.query<
        TDeliverySchedulesImportResponse,
        string
      >({
        query: (orderId) => ({
          method: 'GET',
          url: `seller/order/${orderId}/getInvoiceUrl`,
        }),
      }),
      generateInvoice: builder.query<
        TDeliverySchedulesImportResponse,
        string
      >({
        query: (orderId) => ({
          method: 'GET',
          url: `seller/order/${orderId}/invoice`,
        }),
      }),
      getOrderSAPResponse: builder.query<
        TOrderSAPResponse,
        number
      >({
        query: (orderId) => ({
          method: 'GET',
          url: 'order/sap-response',
          params: {
            id: orderId,
          },
        }),
      }),
      orderTrigger: builder.mutation<void, { id: number }>({
        query: (data) => ({
          method: 'PUT',
          url: 'retailer/sales/orders/retryCount',
          data,
        }),
        invalidatesTags: ['LIST', 'COUNT'],
      }),
    }),
  });

export const {
  useGetOrderHistoryListQuery,
  useGetOrdersStatusCountQuery,
  useEddDelayImportMutation,
  useMarkOrdersForDispatchMutation,
  useLazyExportOrdersQuery,
  useUploadInvoiceMutation,
  useGetFAListQuery,
  useUpdateOrderMutation,
  useGetOrderDetailsQuery,
  useCancelOrderMutation,
  useCancelOrderV2Mutation,
  useOrderActionDocumentUploadMutation,
  useReturnCompleteOrderMutation,
  useMarkPreInvoicedMutation,
  useLazyGetOrderFailureResponseQuery,
  useGetAllPaymentMethodsQuery,
  useIsOrderExpiryExtendedQuery,
  useLazyGetAllOrdersForMasterIdQuery,
  useExtendExpiryMutation,
  useLazyGetInvoiceQuery,
  useGetInvoiceQuery,
  useLazyGenerateInvoiceQuery,
  useLazyGetOrderSAPResponseQuery,
  useOrderTriggerMutation,
} = ordersService;
